import React from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { IconButton, Skeleton } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";

import PageOption from "../../PageTypes/PageOption";

import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";
import Tooltip from "@mui/material/Tooltip";
import OverviewSecondaryStatsProps from "./OverviewSecondaryStatsProps";

import MoreInfoPopover from "./MoreInfoPopover";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";

import { useContext } from "react";
import PersonalizationContext from "../../PersonalizationComponents/PersonalizationContext";

const enterToolTipDelay = 400;

type OverviewConfigDisplayProps = {
  secondaryStats: OverviewSecondaryStatsProps;
  setPage: (newPage: PageOption) => void;
  selectedStrategyName: string | undefined;
  selectedScenarioName: string | undefined;
  withholdingOn: boolean;
  withholdingRate: number;
  firstWithholdingCheckDone: boolean;
};

function OverviewConfigDisplay(props: OverviewConfigDisplayProps) {
  const secondaryStats = props.secondaryStats;

  const navigate = useNavigate();

  const { personalizationConfig, setPersonalizationConfig } = useContext(PersonalizationContext);
  const isCoinbaseSandbox = personalizationConfig.branding === "coinbase";

  return (
    <Box
      sx={{
        flexGrow: 0,
        sizing: "border-box",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          sizing: "border-box",
          display: "flex",
          flexDirection: "row",
          gap: isMobile ? 0 : 2,
          justifyContent: "space-between",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}
      >
        {[
          [
            {
              description: "Next Projected Trade in",
              value:
                secondaryStats.daysUntilNextTrade === null || secondaryStats.daysUntilNextTrade < 0 ? (
                  "∞ days"
                ) : (
                  <MyCountUp
                    value={secondaryStats.daysUntilNextTrade}
                    lastFullyDrawnValue={secondaryStats.lastDrawnDaysUntilNextTrade ?? 365}
                    setLastFullyDrawnValue={secondaryStats.setLastDrawnDaysUntilNextTrade}
                    suffix={
                      secondaryStats.lastDrawnDaysUntilNextVest === 1 && secondaryStats.daysUntilNextVest === 1
                        ? " day"
                        : " days"
                    }
                    negativeIsError
                  />
                ),
              more_info_title: secondaryStats.nextTradeMoreInfoTitle,
              more_info_text: secondaryStats.nextTradeMoreInfo,
              tooltip_text:
                "This is how many days until your next strategy trade is projected with the current strategy and forecast settings. You can change these below",
            },
            {
              description: "Selected Trading Strategy",
              value: props.selectedStrategyName ? props.selectedStrategyName : "-",
              tooltip:
                "This is the strategy you have configured to manage your stock and will automatically send you notifications when it makes trades.",
              loading: false,
              bold: false,
              edit: () => navigate("/strategies?edit=locked_strategy"),
            },
          ],
          [
            {
              description: "Vested Stock Sold (est)",
              value: (
                <MyCountUp
                  value={secondaryStats.valueSold}
                  lastFullyDrawnValue={secondaryStats.lastDrawnValueSold}
                  setLastFullyDrawnValue={secondaryStats.setLastDrawnValueSold}
                  prefix="$"
                  negativeIsError
                  error={secondaryStats.statsError}
                />
              ),
              more_info_title: undefined,
              more_info_text: undefined,
              tooltip_text:
                "This is the calculated value of all stock that has been sold to date. This is currently approximated as evenly split across all vests. Brokerage integrations are coming soon to improve this!",
            },
            {
              description: "Selected Forecast Projections",
              tooltip:
                "This is the current one of your forecast projection models that is used to estimate potential future values of your stock in the above chart. Please note that future charts are not guaranteed and are based on the current model of your inputs and market conditions. This model shows how your stock would develop under this future.",
              value: props.selectedScenarioName ? props.selectedScenarioName : "-",
              loading: false,
              bold: false,
              edit: () => navigate("/forecasts?edit=locked_forecast"),
            },
          ],
          [
            {
              description: isCoinbaseSandbox ? "Next ESPP Action in" : "Next Vesting in",
              value:
                (secondaryStats.daysUntilNextVest === null || secondaryStats.daysUntilNextVest < 0) &&
                !secondaryStats.statsError ? (
                  "∞ days"
                ) : (
                  <MyCountUp
                    value={secondaryStats.daysUntilNextVest}
                    lastFullyDrawnValue={secondaryStats.lastDrawnDaysUntilNextVest ?? 365}
                    setLastFullyDrawnValue={secondaryStats.setLastDrawnDaysUntilNextVest}
                    suffix={
                      secondaryStats.lastDrawnDaysUntilNextVest === 1 && secondaryStats.daysUntilNextVest === 1
                        ? " day"
                        : " days"
                    }
                    negativeIsError
                    error={secondaryStats.statsError}
                  />
                ),
              more_info_title: secondaryStats.nextVestMoreInfoTitle,
              more_info_text: secondaryStats.nextVestMoreInfo,
              tooltip_text: "This is how many days until your next vesting tranche.",
            },
            {
              description: "Tax Withholding",
              value: isCoinbaseSandbox
                ? "(Disabled in sandbox)"
                : props.withholdingOn
                ? "RSUs net of " +
                  (props.withholdingRate >= 0
                    ? props.withholdingRate.toLocaleString("en-US", { style: "percent", maximumFractionDigits: 0 }) +
                      " "
                    : "") +
                  "withholding"
                : "Disabled in profile",
              tooltip:
                "This is your current tax withholding setting from your profile. Viewing values net of withholding is most useful when you've entered an accurate tax withholding rate for your shares. Please consult a tax specialist if you're uncertain what is appropriate for your situation, these values are provided for illustration only and every tax situation is different. However, there is no point in viewing the value from shares you'll never receive. If this is disabled, values throughout all views will be gross.",
              loading: !props.firstWithholdingCheckDone,
              bold: false,
              edit: () => navigate("/profile?edit=true"),
            },
          ],
        ].map((labels, index) => {
          const stat_label = labels[0];
          const label = labels[1];
          const stat_contents = (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                sizing: "border-box",
              }}
            >
              <Tooltip
                title={stat_label.tooltip_text}
                placement="top-start"
                arrow
                enterDelay={enterToolTipDelay}
                sx={{ flexGrow: 1 }}
              >
                <Typography variant="h6" noWrap sx={{ color: "info.main" }}>
                  {stat_label.description}
                </Typography>
              </Tooltip>
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Typography variant="h4" noWrap>
                  {props.secondaryStats.plotLoading ? <Skeleton width={100} /> : stat_label.value}
                </Typography>
                {stat_label.more_info_text ? (
                  <Box sx={{ alignSelf: "center", zIndex: 1 }}>
                    <MoreInfoPopover title={stat_label.more_info_title} text={stat_label.more_info_text} />
                  </Box>
                ) : null}
              </Box>
            </Box>
          );

          const contents = (
            <Tooltip key={index} title={label.tooltip} placement="top-end" arrow enterDelay={enterToolTipDelay}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  fontWeight: label.bold ? 500 : undefined,
                }}
              >
                <Box sx={{ flexGrow: 1, whiteSpace: "nowrap" }}>
                  <Typography variant="h6" noWrap sx={{ color: "info.main" }}>
                    {label.description ? label.description : <Skeleton width={100} />}
                    {label.edit === undefined ? null : (
                      <IconButton onClick={label.edit} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ whiteSpace: "nowrap" }}>
                  <Typography variant="h5" noWrap>
                    {label.loading ? <Skeleton width={50} /> : label.value}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          );

          return (
            <Box key={index} sx={{ display: "flex", flexDirection: "column" }}>
              {stat_contents}
              {contents}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
export default OverviewConfigDisplay;
